import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { 
  FaArrowLeft, FaEnvelope, FaCommentDots, FaUser,
  FaPaperPlane, FaCheck, FaClock, FaHeadset,
  FaTwitter, FaTelegram, FaWhatsapp, FaInfoCircle,
  FaExclamationTriangle, FaSpinner
} from 'react-icons/fa';
import toast from 'react-hot-toast';
import LoadingSpinner from '../components/common/LoadingSpinner';
import './LegalPage.css';

const Contact = () => {
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  
  const { 
    register, 
    handleSubmit, 
    reset,
    formState: { errors } 
  } = useForm();

  const onSubmit = async (data) => {
    setSubmitting(true);
    
    try {
      // In production, this would POST to a contact endpoint
      // For now, we simulate a successful submission
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      console.log('Contact form submission:', data);
      
      setSubmitted(true);
      reset();
      toast.success('Message sent successfully!');
      
      // Reset submitted state after 5 seconds
      setTimeout(() => setSubmitted(false), 5000);
    } catch (error) {
      console.error('Contact error:', error);
      toast.error('Failed to send message. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="legal-page">
      <div className="legal-container">
        <Link to="/" className="back-link">
          <FaArrowLeft /> Back to Home
        </Link>

        {/* Header */}
        <div className="legal-header">
          <div className="header-icon-wrap gold">
            <FaHeadset className="header-icon" />
          </div>
          <h1>Contact Us</h1>
          <p className="legal-subtitle">
            We're here to help. Reach out to us anytime.
          </p>
          <p className="legal-intro">
            Have a question, issue, or feedback? Fill out the form below 
            or reach us through our social channels.
          </p>
        </div>

        {/* Quick Contact Cards */}
        <div className="contact-cards">
          <a href="mailto:support@xpunters.com" className="contact-card">
            <FaEnvelope className="contact-card-icon" />
            <h4>Email Us</h4>
            <p>support@xpunters.com</p>
            <span className="contact-response">Response within 48h</span>
          </a>
          <div className="contact-card">
            <FaClock className="contact-card-icon" />
            <h4>Response Time</h4>
            <p>Mon–Sat: 8am – 8pm WAT</p>
            <span className="contact-response">Closed on Sundays</span>
          </div>
          <div className="contact-card">
            <FaHeadset className="contact-card-icon" />
            <h4>Priority Support</h4>
            <p>Available for Punter+ members</p>
            <span className="contact-response">Faster response</span>
          </div>
        </div>

        {/* Social Links */}
        {/* <div className="legal-summary">
          <h3><FaInfoCircle /> Follow Us</h3>
          <div className="social-links">
            <a 
              href="https://twitter.com/xpunters" 
              target="_blank" 
              rel="noopener noreferrer"
              className="social-link twitter"
            >
              <FaTwitter /> Twitter / X
            </a>
            <a 
              href="https://t.me/xpunters" 
              target="_blank" 
              rel="noopener noreferrer"
              className="social-link telegram"
            >
              <FaTelegram /> Telegram
            </a>
            <a 
              href="https://wa.me/2348000000000" 
              target="_blank" 
              rel="noopener noreferrer"
              className="social-link whatsapp"
            >
              <FaWhatsapp /> WhatsApp
            </a>
          </div>
        </div> */}

        {/* Contact Form */}
        <div className="contact-form-wrapper">
          <h2 className="contact-form-title">
            <FaPaperPlane /> Send Us a Message
          </h2>
          <p className="contact-form-subtitle">
            Fill out the form below and we'll get back to you as soon as possible.
          </p>

          {submitted ? (
            <div className="contact-success">
              <div className="success-icon-wrap">
                <FaCheck className="success-icon" />
              </div>
              <h3>Message Sent!</h3>
              <p>
                Thank you for reaching out. We've received your message and 
                will respond within 48 hours.
              </p>
              <button 
                type="button"
                onClick={() => setSubmitted(false)}
                className="btn btn-secondary"
              >
                Send Another Message
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit(onSubmit)} className="contact-form">
              <div className="form-row">
                <div className="form-group">
                  <label>
                    <FaUser /> Your Name *
                  </label>
                  <input
                    type="text"
                    placeholder="Enter your full name"
                    {...register('name', { 
                      required: 'Name is required',
                      minLength: {
                        value: 2,
                        message: 'Name must be at least 2 characters'
                      }
                    })}
                    className={errors.name ? 'input-error' : ''}
                  />
                  {errors.name && (
                    <span className="error-message">{errors.name.message}</span>
                  )}
                </div>

                <div className="form-group">
                  <label>
                    <FaEnvelope /> Email Address *
                  </label>
                  <input
                    type="email"
                    placeholder="Enter your email"
                    {...register('email', { 
                      required: 'Email is required',
                      pattern: {
                        value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                        message: 'Please enter a valid email'
                      }
                    })}
                    className={errors.email ? 'input-error' : ''}
                  />
                  {errors.email && (
                    <span className="error-message">{errors.email.message}</span>
                  )}
                </div>
              </div>

              <div className="form-group">
                <label>
                  <FaCommentDots /> Subject *
                </label>
                <select
                  {...register('subject', { required: 'Subject is required' })}
                  className={errors.subject ? 'input-error' : ''}
                >
                  <option value="">Select a subject</option>
                  <option value="general">General Inquiry</option>
                  <option value="technical">Technical Support</option>
                  <option value="payment">Payment Issue</option>
                  <option value="withdrawal">Withdrawal Issue</option>
                  <option value="account">Account Help</option>
                  <option value="bug">Report a Bug</option>
                  <option value="feedback">Feedback & Suggestions</option>
                  <option value="business">Business Partnership</option>
                  <option value="other">Other</option>
                </select>
                {errors.subject && (
                  <span className="error-message">{errors.subject.message}</span>
                )}
              </div>

              <div className="form-group">
                <label>
                  <FaCommentDots /> Message *
                </label>
                <textarea
                  placeholder="Describe your question or issue in detail..."
                  {...register('message', { 
                    required: 'Message is required',
                    minLength: {
                      value: 10,
                      message: 'Message must be at least 10 characters'
                    },
                    maxLength: {
                      value: 2000,
                      message: 'Message is too long (max 2000 characters)'
                    }
                  })}
                  rows={6}
                  className={errors.message ? 'input-error' : ''}
                />
                {errors.message && (
                  <span className="error-message">{errors.message.message}</span>
                )}
              </div>

              <div className="form-info-row">
                <FaExclamationTriangle className="info-icon" />
                <span>
                  For payment or withdrawal issues, please include your 
                  transaction reference in the message.
                </span>
              </div>

              <button 
                type="submit" 
                className="btn btn-primary btn-lg contact-submit"
                disabled={submitting}
              >
                {submitting ? (
                  <>
                    <FaSpinner className="spin" /> Sending...
                  </>
                ) : (
                  <>
                    <FaPaperPlane /> Send Message
                  </>
                )}
              </button>
            </form>
          )}
        </div>

        {/* FAQ Link */}
        <div className="legal-footer">
          <h3>Looking for quick answers?</h3>
          <p>
            Check out our frequently asked questions or contact us directly 
            for assistance.
          </p>
          <div className="legal-footer-actions">
            <Link to="/terms" className="btn btn-secondary">
              Terms of Service
            </Link>
            <Link to="/privacy" className="btn btn-secondary">
              Privacy Policy
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Contact;